import { useQuery } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";

import { useFilteredDeployments } from "@/api/resources/deployments/hooks";
import { apiConfigDeviceTags, createDeviceTag, deleteDeviceTag, updateDeviceTag } from "@/api/resources/deviceTags/api";
import { deviceTagsQueries, deviceTagsQueryKeys } from "@/api/resources/deviceTags/queries";
import {
  selectorCurrentDeviceTags,
  selectorDeviceTagsLeveled,
  selectorDeviceTagsLeveledFlattened,
  selectorFilteredDeviceTags,
} from "@/api/resources/deviceTags/selectors";
import { useGlobalFilters } from "@/hooks/useGlobalFilters";
import i18n from "@/providers/i18n";
import { spawnCreateMutationHook, spawnDeleteMutationHook, spawnUpdateMutationHook } from "@/utils/api";

const entity = i18n.t("device-tag");

export const useDeviceTags = () => {
  return useQuery(deviceTagsQueries.listAll());
};

export const useDeviceTag = (id?: string) => {
  return useQuery(deviceTagsQueries.byId(id));
};

export const useDeviceTagChildren = (device_tag_id?: string) => {
  const { data: filteredDeployments } = useFilteredDeployments();
  const workspace_ids = filteredDeployments?.map((deployment) => deployment.workspace_id);
  return useQuery(deviceTagsQueries.listChildren(workspace_ids ?? [], device_tag_id));
};

export const useCurrentDeviceTags = () => {
  const { workspace_id } = useParams({ strict: false });
  return useQuery({
    ...deviceTagsQueries.listAll(),
    select: (deviceTags) => selectorCurrentDeviceTags({ currentWorkspaceId: workspace_id, deviceTags }),
  });
};

export const useCurrentDeviceTagsLeveled = () => {
  const { workspace_id } = useParams({ strict: false });
  return useQuery({
    ...deviceTagsQueries.listAll(),
    select: (deviceTags) =>
      selectorDeviceTagsLeveled(selectorCurrentDeviceTags({ currentWorkspaceId: workspace_id, deviceTags })),
  });
};

export const useCurrentDeviceTagsLeveledFlattened = () => {
  const { workspace_id } = useParams({ strict: false });
  return useQuery({
    ...deviceTagsQueries.listAll(),
    select: (deviceTags) =>
      selectorDeviceTagsLeveledFlattened(
        selectorDeviceTagsLeveled(selectorCurrentDeviceTags({ currentWorkspaceId: workspace_id, deviceTags })),
      ),
  });
};

export const useFilteredDeviceTags = () => {
  const { options } = useGlobalFilters();
  const { data: filteredDeployments } = useFilteredDeployments();
  return useQuery({
    ...deviceTagsQueries.listAll(),
    select: (deviceTags) =>
      selectorFilteredDeviceTags({
        deviceTags,
        filteredDeployments: filteredDeployments ?? [],
        options,
      }),
  });
};

export const useFilteredDeviceTagsLeveled = () => {
  const { options } = useGlobalFilters();
  const { data: filteredDeployments } = useFilteredDeployments();
  return useQuery({
    ...deviceTagsQueries.listAll(),
    select: (deviceTags) =>
      selectorDeviceTagsLeveled(
        selectorFilteredDeviceTags({
          deviceTags,
          filteredDeployments: filteredDeployments ?? [],
          options,
        }),
      ),
  });
};

export const useFilteredDeviceTagsLeveledFlattened = () => {
  const { options } = useGlobalFilters();
  const { data: filteredDeployments } = useFilteredDeployments();
  return useQuery({
    ...deviceTagsQueries.listAll(),
    select: (deviceTags) =>
      selectorDeviceTagsLeveledFlattened(
        selectorDeviceTagsLeveled(
          selectorFilteredDeviceTags({
            deviceTags,
            filteredDeployments: filteredDeployments ?? [],
            options,
          }),
        ),
      ),
  });
};

export const useCreateDeviceTag = spawnCreateMutationHook({
  mutationFn: createDeviceTag,
  notifyEntity: entity,
  analyticsEntity: apiConfigDeviceTags.table,
  invalidationQueryKeys: [deviceTagsQueryKeys.lists()],
});
export const useUpdateDeviceTag = spawnUpdateMutationHook({
  mutationFn: updateDeviceTag,
  notifyEntity: entity,
  analyticsEntity: apiConfigDeviceTags.table,
  invalidationQueryKeys: [deviceTagsQueryKeys.lists()],
});
export const useDeleteDeviceTag = spawnDeleteMutationHook({
  mutationFn: deleteDeviceTag,
  notifyEntity: entity,
  analyticsEntity: apiConfigDeviceTags.table,
  invalidationQueryKeys: [deviceTagsQueryKeys.lists()],
});
